import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { triggerAppliesToBuyer } from '@dill/dill-shared';
import {
  CollectionSequenceEnrollment,
  enrollCollectionSequenceEnrollment,
  getBuyerCollectionSequenceEnrollments,
  getSupplierCollectionSequences,
  pauseCollectionSequenceEnrollment,
  resumeCollectionSequenceEnrollment,
  unenrollCollectionSequenceEnrollment,
} from '../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../redux/services/collectionTriggerService';
import { DillButton, DillSelect } from '../../../general/Dill';
import { useCollectionBuyerEngagements } from './useCollectionBuyerEngagements';

interface BuyerCollectionSequenceEnrollmentsProps {
  supplierId: string;
  buyerId: string;
  buyerName?: string | null;
}

// Shows this buyer's current sequence enrollment (at most one, given the global mutual-exclusion
// rule) with pause/resume/unenroll, and lets a staff member manually enroll the buyer directly
// into any sequence they're eligible for - enrollment begins executing immediately. Sequences the
// buyer is blocked from (already engaged on a trigger's target list, or in another sequence) are
// shown with the reason rather than silently hidden, so it's clear why enrolling isn't offered.
export const BuyerCollectionSequenceEnrollments = ({
  supplierId,
  buyerId,
  buyerName,
}: BuyerCollectionSequenceEnrollmentsProps) => {
  const dispatch = useAppDispatch();
  const { sequences } = useAppSelector((state) => state.collectionSequences);
  const { triggers } = useAppSelector((state) => state.collectionTriggers);
  const { engagementsByBuyerId, refresh: refreshEngagements } =
    useCollectionBuyerEngagements(supplierId);
  const [enrollments, setEnrollments] = useState<CollectionSequenceEnrollment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionPending, setIsActionPending] = useState(false);
  const [pickedSequenceId, setPickedSequenceId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refresh = async () => {
    if (!supplierId || !buyerId) return;
    setIsLoading(true);
    const [enrollmentsResult] = await Promise.all([
      dispatch(getBuyerCollectionSequenceEnrollments({ supplierId, buyerId })),
      dispatch(getSupplierCollectionSequences({ supplierId })),
      dispatch(getSupplierCollectionTriggers({ supplierId })),
    ]);
    const payload = enrollmentsResult.payload as {
      success?: boolean;
      data?: CollectionSequenceEnrollment[];
    } | null;
    setEnrollments(payload?.success && payload.data ? payload.data : []);
    setIsLoading(false);
    refreshEngagements();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, buyerId]);

  const runAction = async (action: Promise<{ payload: unknown }>) => {
    setIsActionPending(true);
    setErrorMessage(null);
    const result = await action;
    const payload = result.payload as { success?: boolean; errors?: { msg?: string }[] } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(payload?.errors?.[0]?.msg || 'Something went wrong. Please try again.');
    }
    setIsActionPending(false);
    refresh();
  };

  const handleEnroll = () => {
    if (!pickedSequenceId) return;
    const sequenceId = pickedSequenceId;
    setPickedSequenceId(null);
    runAction(dispatch(enrollCollectionSequenceEnrollment({ supplierId, sequenceId, buyerId })));
  };

  const handlePause = (sequenceId: string) =>
    runAction(dispatch(pauseCollectionSequenceEnrollment({ supplierId, sequenceId, buyerId })));

  const handleResume = (sequenceId: string) =>
    runAction(dispatch(resumeCollectionSequenceEnrollment({ supplierId, sequenceId, buyerId })));

  const handleUnenroll = (sequenceId: string) =>
    runAction(dispatch(unenrollCollectionSequenceEnrollment({ supplierId, sequenceId, buyerId })));

  const enrolledSequenceIds = useMemo(
    () => new Set(enrollments.map((e) => e.sequence.id)),
    [enrollments]
  );

  // A buyer already SEQUENCE_ENROLLED (shown above via `enrollments`) or TRIGGER_TARGET elsewhere
  // can't be manually enrolled anywhere else - the whole picker is disabled with a reason rather
  // than offering choices that would just be rejected by the backend. We don't rely solely on the
  // engagement index here - it's a separate doc that can lag behind a sibling component's own
  // action (e.g. adding this buyer to a trigger's target list refreshes that component's own
  // engagement hook instance, not this one) - so we also cross-check the raw trigger target lists
  // directly, same guard pattern used in the trigger/sequence form modals.
  const existingEngagement = engagementsByBuyerId[buyerId];
  const isOnAnyTriggerTargetList = useMemo(
    () => triggers.some((t) => triggerAppliesToBuyer(t, buyerId)),
    [triggers, buyerId]
  );
  const isBlockedElsewhere =
    isOnAnyTriggerTargetList || (!!existingEngagement && enrollments.length === 0);

  // Any active sequence the buyer isn't already enrolled in is enrollable directly from here - the
  // enroll action adds them to the sequence's own `buyerIds` as part of the same request if
  // they're not already on it (see enrollCollectionSequenceEnrollmentController), so there's no
  // need to have pre-added them via the sequence's own form first.
  const enrollableSequences = useMemo(
    () => sequences.filter((s) => s.isActive && !enrolledSequenceIds.has(s.id)),
    [sequences, enrolledSequenceIds]
  );

  if (isLoading) return null;
  if (enrollments.length === 0 && enrollableSequences.length === 0 && !isBlockedElsewhere)
    return null;

  const name = buyerName || 'This customer';

  return (
    <div className="flex flex-col gap-2 mb-2">
      <p className="text-xs font-semibold text-gray-700 m-0">Sequence enrollment</p>

      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      {enrollments.map(({ sequence, progress }) => (
        <div
          key={sequence.id}
          className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          <p className="text-xs text-blue-800 m-0">
            <span className="font-medium">{name}</span> is enrolled in{' '}
            <span className="font-medium">{sequence.name}</span>.
            {progress.status === 'PAUSED' && (
              <span className="ml-1.5 text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5 align-middle">
                Paused
              </span>
            )}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            {progress.status === 'ACTIVE' ? (
              <button
                type="button"
                disabled={isActionPending}
                onClick={() => handlePause(sequence.id)}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer p-0 disabled:opacity-50 disabled:cursor-not-allowed">
                Pause
              </button>
            ) : (
              <button
                type="button"
                disabled={isActionPending}
                onClick={() => handleResume(sequence.id)}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer p-0 disabled:opacity-50 disabled:cursor-not-allowed">
                Resume
              </button>
            )}
            <button
              type="button"
              disabled={isActionPending}
              onClick={() => handleUnenroll(sequence.id)}
              className="text-xs font-medium text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0 disabled:opacity-50 disabled:cursor-not-allowed">
              Unenroll
            </button>
          </div>
        </div>
      ))}

      {isBlockedElsewhere && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-3 py-2 m-0">
          {name} is already{' '}
          {isOnAnyTriggerTargetList || existingEngagement?.type === 'TRIGGER_TARGET'
            ? 'on a trigger’s target list'
            : 'enrolled in another sequence'}
          . Remove them from it first to enroll them here.
        </p>
      )}

      {!isBlockedElsewhere && enrollableSequences.length > 0 && (
        <div className="flex items-center gap-2 border border-dashed border-gray-200 rounded-lg px-3 py-2">
          <div className="flex-1 min-w-0">
            <DillSelect
              value={pickedSequenceId ?? ''}
              onChange={setPickedSequenceId}
              options={enrollableSequences.map((s) => ({ label: s.name, value: s.id }))}
              placeholder="Manually enroll in a sequence…"
            />
          </div>
          <DillButton
            label="Enroll"
            onClick={handleEnroll}
            isDisabled={!pickedSequenceId || isActionPending}
            height={34}
          />
        </div>
      )}
    </div>
  );
};

export default BuyerCollectionSequenceEnrollments;
